// =============================================================================
// ResetPassword — handles Supabase recovery email link at /reset-password
// =============================================================================

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase.js';

export default function ResetPassword() {
  const navigate = useNavigate();
  // S100 Build 2 — ?mode=invite comes alive. team.js has sent it since S83 and
  // the page ignored it (the cut was recorded at the generateLink call site).
  // Invite mode reads as a WELCOME — and for a player invite, recruit-direct:
  // the kid is "you", the wording is "set your password", not staff copy.
  const [searchParams] = useSearchParams();
  const inviteMode = searchParams.get('mode') === 'invite';
  const requestedNext = searchParams.get('next');
  const safeNext = requestedNext?.startsWith('/') && !requestedNext.startsWith('//') ? requestedNext : null;
  const [validLink, setValidLink] = useState(null); // null = checking
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setValidLink(!!data.user);
    });
  }, []);

  async function onSubmit(e) {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setLoading(false);

    if (updateError) {
      setError(updateError.message);
      return;
    }
    // S100 Build 2 — route by WHO just set a password, not by a constant. A
    // player lands on their own page; staff keep the exact landing they had.
    const { data: userData } = await supabase.auth.getUser();
    const uid = userData?.user?.id;
    if (uid) {
      const { data: playerRow } = await supabase
        .from('player_profiles').select('recruit_id').eq('user_id', uid).maybeSingle();
      if (playerRow) {
        navigate(safeNext || '/my-profile', { replace: true });
        return;
      }
    }
    navigate('/huddler', { replace: true });
  }

  if (validLink === null) {
    return (
      <div className="min-h-screen bg-hunter-deep flex items-center justify-center">
        <p className="text-white text-sm">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-hunter-deep flex items-center justify-center p-4">
      <div className="w-full max-w-md">

        <div className="text-center mb-8">
          {/* Invite mode is the KID's front door — "Front Office" is staff
              vocabulary and stays off it. */}
          <h1 className="text-white text-3xl font-display font-black tracking-tight">
            {inviteMode ? 'Coach Cole Football' : 'CCF Front Office'}
          </h1>
          <p className="text-mid-green text-xs tracking-[0.3em] uppercase font-bold mt-2">
            {inviteMode ? 'Player Profile' : 'Coach Cole Football'}
          </p>
        </div>

        {!validLink ? (
          <div className="card space-y-4 text-center">
            <h2 className="text-xl font-display font-bold text-hunter">Link Expired</h2>
            <p className="text-sm text-gray-600">
              {inviteMode
                ? 'This invite link is no longer valid. Ask your CCF coach to send a new one.'
                : 'This reset link is no longer valid.'}
            </p>
            <button type="button" onClick={() => navigate('/login')} className="btn-primary w-full">
              Back to Login
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="card space-y-4">
            <h2 className="text-xl font-display font-bold text-hunter">
              {inviteMode ? 'Welcome — set your password' : 'Set New Password'}
            </h2>
            {inviteMode && (
              <p className="text-sm text-gray-600">
                Pick a password and you&apos;re in. You can add your photo, bio, film, and numbers
                on the next screen.
              </p>
            )}

            <div>
              <label className="block text-sm font-bold mb-1">New Password</label>
              <input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="input"
                autoComplete="new-password"
              />
            </div>

            <div>
              <label className="block text-sm font-bold mb-1">Confirm Password</label>
              <input
                type="password"
                required
                minLength={8}
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                className="input"
                autoComplete="new-password"
              />
            </div>

            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
                {error}
              </div>
            )}

            <button type="submit" disabled={loading} className="btn-primary w-full">
              {loading ? 'Updating…' : 'Update Password'}
            </button>
          </form>
        )}

      </div>
    </div>
  );
}
